import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';
import { cancelSubscriptionScheduledChange } from '../../../common/fetch.js';
import { useSearchParams } from '../../../common/hooks/useSearchParams';
import { t, userMsg } from '../../../common/i18n/index.js';
import { SubscriptionType } from '../../../generated/api/subscriptionType.js';
import { addNotification } from '../../../public/site/notificationStore';
import { ChangeContractOwnerView } from '../Accordions/ChangeContractOwnerAccordion.js';
import {
  createPurposeOfUseObj,
  getNumberPrivacyCorporate,
  getNumberPrivacyMobile,
} from '../subscriptionDetailsUtils.js';
import { HighlightField } from './HighlightedValue.js';
import { DeleteScheduledChangeDialog } from './ScheduledChangeDialogs.js';
import { SubscriptionVersion } from './SubscriptionScheduledChangeDisclaimer.js';
import { SubscriptionVersionSelector } from './SubscriptionVersionSelector.js';
import { UserInfoView } from './UserInfoView.js';
import type { HighlightedField } from './HighlightedValue.js';
import type { CompanyInfoResponse } from '../../../generated/api/companyInfoResponse.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { Subscription } from '../../../generated/api/subscription.js';
import type { SubscriptionPbxDetails } from '../../../generated/api/subscriptionPbxDetails.js';
import type { SubscriptionScheduledChange } from '../../../generated/api/subscriptionScheduledChange.js';

interface BillingAccountAndUserInfoViewProps {
  subscription: Subscription;
  companyInfo?: CompanyInfoResponse;
  originalSubscription: Subscription;
  isScheduledView: boolean;
  contacts: Contact[];
  pbxType?: SubscriptionPbxDetails.PbxTypeEnum;
  scheduledChange?: SubscriptionScheduledChange;
  subscriptionVersion: SubscriptionVersion;
  onSubscriptionVersionChange: (version: SubscriptionVersion) => void;
}

const getChangedFields = (
  original: Subscription,
  scheduled: Subscription,
  effectiveNumberPrivacyMobile: string | undefined,
  activeNumberPrivacyMobile: string | undefined,
  effectiveNumberPrivacyCorporate: string | undefined,
  activeNumberPrivacyCorporate: string | undefined
): readonly HighlightedField[] => {
  const changed: HighlightedField[] = [];
  const isDifferent = (a: string | undefined, b: string | undefined) => (a || '') !== (b || '');

  if (isDifferent(original.billingAccountId, scheduled.billingAccountId)) {
    changed.push(HighlightField.BILLING_ACCOUNT);
  }
  if (isDifferent(original.subscriptionContactId, scheduled.subscriptionContactId)) {
    changed.push(HighlightField.CONTACT);
  }
  if (isDifferent(original.subscriptionPurposeOfUse, scheduled.subscriptionPurposeOfUse)) {
    changed.push(HighlightField.PURPOSE_OF_USE);
  }
  if (isDifferent(original.costCenter, scheduled.costCenter)) {
    changed.push(HighlightField.COST_CENTER);
  }
  if (isDifferent(original.subscriptionReference, scheduled.subscriptionReference)) {
    changed.push(HighlightField.REFERENCE_ID);
  }
  if (effectiveNumberPrivacyMobile !== activeNumberPrivacyMobile) {
    changed.push(HighlightField.NUMBER_PRIVACY_MOBILE);
  }
  if (effectiveNumberPrivacyCorporate !== activeNumberPrivacyCorporate) {
    changed.push(HighlightField.NUMBER_PRIVACY_CORPORATE);
  }
  return changed;
};

export const BillingAccountAndUserInfoView = ({
  subscription,
  originalSubscription,
  isScheduledView,
  companyInfo,
  contacts,
  pbxType,
  scheduledChange,
  subscriptionVersion,
  onSubscriptionVersionChange,
}: BillingAccountAndUserInfoViewProps) => {
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);

  const onDeleteScheduledChange = async () => {
    if (scheduledChange) {
      try {
        await cancelSubscriptionScheduledChange(subscription.subscriptionId, scheduledChange.changeRequestId);
        setShowDeleteConfirmDialog(false);
        revalidate();
        navigate(`?mdmId=${mdmId}`, { replace: true });
      } catch {
        setShowDeleteConfirmDialog(false);
        addNotification(t.ORRM('Submit change order failed'), 'error');
      }
    }
  };

  const isRingSubscription = subscription.subscriptionType === SubscriptionType.MOBILE_PBX_END_USER_SERVICE;

  const purposeOfUseOrContact = createPurposeOfUseObj(subscription);
  const numberPrivacyMobile = getNumberPrivacyMobile(subscription, purposeOfUseOrContact);
  const numberPrivacyCorporate = getNumberPrivacyCorporate(subscription, purposeOfUseOrContact);

  const changeRequest = scheduledChange?.subscriptionChangeOwnerAccountRequest;
  const effectiveNumberPrivacyMobile =
    isScheduledView && changeRequest?.voiceDirectoryDetails
      ? changeRequest.voiceDirectoryDetails.numberDirectory
      : numberPrivacyMobile;
  const effectiveNumberPrivacyCorporate =
    isScheduledView && changeRequest?.pbxDirectoryDetails
      ? changeRequest.pbxDirectoryDetails.numberDirectory
      : numberPrivacyCorporate;

  const changedFields = isScheduledView
    ? getChangedFields(
        originalSubscription,
        subscription,
        effectiveNumberPrivacyMobile,
        getNumberPrivacyMobile(originalSubscription, createPurposeOfUseObj(originalSubscription)),
        effectiveNumberPrivacyCorporate,
        getNumberPrivacyCorporate(originalSubscription, createPurposeOfUseObj(originalSubscription))
      )
    : [];

  return (
    <>
      {scheduledChange && (
        <SubscriptionVersionSelector
          scheduledChangeTimestamp={scheduledChange.scheduledChangeTimestamp}
          subscriptionVersion={subscriptionVersion}
          onSubscriptionVersionChange={onSubscriptionVersionChange}
        />
      )}

      <div className="contract-owner-and-billing-account-wrapper">
        <ChangeContractOwnerView
          subscription={subscription}
          companyInfo={companyInfo}
          billingAccountLink={true}
          changedFields={changedFields}
        />
      </div>

      {isRingSubscription ? (
        <CL.Description
          items={[
            {
              title: t.LUZM(userMsg),
              description: subscription.subscriptionPurposeOfUse,
            },
          ]}
        />
      ) : (
        <UserInfoView
          subscription={subscription}
          contacts={contacts}
          purposeOfUseOrContact={purposeOfUseOrContact}
          numberPrivacyMobile={effectiveNumberPrivacyMobile}
          numberPrivacyCorporate={effectiveNumberPrivacyCorporate}
          pbxType={pbxType}
          changedFields={changedFields}
        />
      )}

      {scheduledChange && subscriptionVersion === SubscriptionVersion.SCHEDULED && (
        <div className="of-scheduled-change-delete-button">
          <CL.Button
            color="light"
            onClick={() => setShowDeleteConfirmDialog(true)}
            icon={<CL.Icon icon="delete" size="m" type="regular" />}
          >
            {t.VT06('Delete change request')}
          </CL.Button>
        </div>
      )}

      {showDeleteConfirmDialog && (
        <DeleteScheduledChangeDialog
          onClose={() => setShowDeleteConfirmDialog(false)}
          onConfirm={onDeleteScheduledChange}
        />
      )}
    </>
  );
};
